<template>
  <b-form novalidate>
    <b-form-group label="CPF" label-for="document">
      <b-input-group>
        <b-input
          id="document"
          v-model="form.document"
          type="search"
          placeholder="Informe CPF do cliente"
          :state="validation.document"
        />
        <b-input-group-append>
          <b-button
            variant="primary"
            :disabled="!form.document"
            @click="onSearch"
          >
            <span class="fas fa-search mr-1" /> Pesquisar
          </b-button>
        </b-input-group-append>
        <b-form-invalid-feedback v-if="$v.form.document.$invalid">
          <span v-if="!$v.form.document.required" class="d-block">
            Informe o cliente
          </span>
          <span v-if="!$v.form.document.isValidCPF" class="d-block">
            Informe um CPF válido
          </span>
        </b-form-invalid-feedback>
      </b-input-group>

      <!-- dica padrão -->
      <div>
        <small v-if="showSearchHint" class="text-danger">
          Clique em Pesquisar para buscar os dados do cliente
        </small>
      </div>

      <!-- AVISO DISCRETO DE REPORTS -->
      <div v-if="reportsCount > 0" class="mt-2 d-flex align-items-center">
        <i
          class="bi bi-exclamation-triangle-fill me-2"
          style="font-size: 1rem"
        ></i>
        <small>
          Este cliente possui
          <b-badge
            v-b-tooltip.hover
            variant="danger"
            class="mx-1"
            title="Quantidade de reports registrados para este cliente"
          >
            {{ reportsCount }}
          </b-badge>
          {{ reportsCount === 1 ? 'aviso registrado' : 'avisos registrados' }}
        </small>
      </div>
    </b-form-group>

    <fieldset v-if="showCustomerForm">
      <b-form-group label-for="name">
        <template #label>
          <div class="d-flex justify-content-between">
            <span> Nome </span>
            <b-checkbox v-if="form.id" v-model="form.updateCustomer">
              Atualizar dados
            </b-checkbox>
          </div>
        </template>
        <b-form-input
          id="name"
          ref="name"
          v-model="form.name"
          :state="validation.name"
          :disabled="!form.updateCustomer"
        />
        <b-form-invalid-feedback v-if="$v.form.name.$invalid">
          Informe o nome
        </b-form-invalid-feedback>
      </b-form-group>

      <b-row>
        <b-col>
          <b-form-group label="Telefone" label-for="phone">
            <b-form-input
              id="phone"
              v-model="form.phone"
              :disabled="!form.updateCustomer"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group label="E-mail" label-for="email">
            <b-form-input
              id="email"
              v-model="form.email"
              type="email"
              :disabled="!form.updateCustomer"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </fieldset>

    <b-form-group label="Período" label-for="period-id">
      <PeriodSelect
        v-model="form.periodId"
        :store-id="storeId"
        :validation="$v.form.periodId"
      />
      <b-form-invalid-feedback v-if="$v.form.periodId.$invalid">
        Informe o período
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      v-if="showCommissionUserSelect"
      label="Funcionária responsável pela venda"
      label-for="commission-user-store-id"
    >
      <b-form-select
        id="commission-user-store-id"
        v-model="form.commissionUserStoreId"
        :options="commissionUserOptions"
        :state="validation.commissionUserStoreId"
      />
      <b-form-invalid-feedback v-if="$v.form.commissionUserStoreId.$invalid">
        Selecione a funcionária responsável pela venda
      </b-form-invalid-feedback>
    </b-form-group>
  </b-form>
</template>

<script>
import { isValidCPF } from '@/helpers/validators'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'
import withFormValidation from '@/mixins/withFormValidation'
import PeriodSelect from '@/components/Periods/PeriodSelect'
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'

export default {
  name: 'ToyRentStartModalForm',
  components: { PeriodSelect },
  mixins: [
    withFormValidation({
      fields: ['document', 'name', 'periodId', 'commissionUserStoreId']
    }),
    withAsyncAction({
      key: 'customer',
      fetcher: {
        methodName: 'fetchCustomerByDocument',
        handler: services.customers.fetchCustomerByDocument
      }
    })
  ],
  validations: {
    form: {
      document: { required, isValidCPF },
      name: { required },
      periodId: { required },
      commissionUserStoreId: {
        required: requiredIf(function () {
          return this.showCommissionUserSelect
        })
      }
    }
  },
  props: {
    toy: { type: Object, required: true },
    submitted: { type: Boolean },
    storeId: { type: [String, Number], default: null },
    value: { type: Object, required: true }
  },
  data() {
    return {
      showCustomerForm: false,
      dirty: false,
      // novo estado: contagem de reports encontrados
      reportsCount: 0,
      storeHasCommissions: false,
      commissionUsers: []
    }
  },
  computed: {
    ...mapGetters('store', [
      'currentStoreId',
      'hasCommissions',
      'isAdmin',
      'isShopping'
    ]),
    form: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    showSearchHint() {
      const { $dirty, $invalid } = this.$v.form
      return $dirty && !$invalid && !this.showCustomerForm
    },
    commissionsEnabled() {
      return (
        this.storeHasCommissions ||
        (Number(this.currentStoreId) === Number(this.storeId) &&
          this.hasCommissions)
      )
    },
    showCommissionUserSelect() {
      return this.commissionsEnabled && (this.isAdmin || this.isShopping)
    },
    commissionUserOptions() {
      return [
        { value: null, text: 'Selecione' },
        ...this.commissionUsers
          .filter((user) => user.role !== 'shopping')
          .map((user) => ({
            value: user.userStoreId,
            text: `${user.name} (${this.formatRole(user.role)})`
          }))
      ]
    }
  },
  watch: {
    storeId() {
      this.loadCommissionData()
    },
    showCommissionUserSelect(value) {
      if (!value) {
        this.form = {
          ...this.form,
          commissionUserStoreId: null
        }
      }
    }
  },
  mounted() {
    this.loadCommissionData()
    if (this.form.document) {
      this.onSearch()
    }
  },
  methods: {
    async loadCommissionData() {
      if (!this.storeId) return

      try {
        const store = await services.stores.fetchStoreById(this.storeId)
        this.storeHasCommissions = Boolean(store?.hasCommissions)
      } catch (error) {
        this.storeHasCommissions = false
      }

      if (this.commissionsEnabled) {
        try {
          this.commissionUsers = await services.users.fetchCommissionableUsers(
            this.storeId
          )
        } catch (error) {
          this.commissionUsers = []
        }
      } else {
        this.commissionUsers = []
      }
    },
    formatRole(role) {
      const labels = {
        admin: 'Admin Geral',
        employer: 'Usuário',
        shopping: 'Shopping'
      }
      return labels[role] || role
    },
    async onSearch() {
      this.$v.form.document.$touch()

      if (!this.$v.form.document.$invalid) {
        try {
          // 1) busca cliente
          const customer = await this.fetchCustomerByDocument(
            this.form.document
          )
          this.updateForm(customer)

          // 2) discretamente busca reports por customerId e atualiza badge
          if (customer?.id) {
            try {
              const { data } =
                await services.customers.fetchCustomerReportsByCustomerId(
                  customer.id
                )
              const list = Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data)
                ? data
                : []
              this.reportsCount = list.length || 0
            } catch (err) {
              // fica silencioso; não quebra o fluxo de aluguel
              this.reportsCount = 0
              // opcional: console.warn('Falha ao buscar reports', err)
            }
          } else {
            this.reportsCount = 0
          }
        } catch (error) {
          if (error?.response?.status === 400) {
            this.form.updateCustomer = true
            setTimeout(() => this.$refs.name.$el.focus(), 100)
            this.$root.$toast.warning(
              'Cliente não encontrado. Preencha os dados para criar um novo cliente'
            )
            this.updateForm()
            this.reportsCount = 0
          }
        } finally {
          this.showCustomerForm = true
        }
      }
    },
    updateForm(customer) {
      this.form = {
        ...this.form,
        id: customer?.id ?? '',
        name: customer?.name ?? '',
        phone: customer?.phone ?? '',
        email: customer?.email ?? ''
      }
    }
  }
}
</script>
